import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../api/client';
import Avatar from './Avatar';
import AlertModal from './AlertModal';
import { CoinIcon, FloatIcon, ScreenTitle } from './CurrencyIcons';
import { iconUrl } from '../utils/assets';
import { useLocale } from '../i18n/LocaleContext';
import { celebrate } from '../utils/fx';
import { useGameAlert } from '../hooks/useGameAlert';
import { useVisibleInterval } from '../hooks/useVisibleInterval';
import { refreshDuel, subscribeDuel } from '../utils/duelStore';

const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

const fmtNum = (v) => {
  const n = num(v);
  if (n >= 100000) return `${Math.round(n / 1000)}k`;
  if (n >= 1000) return n.toFixed(0);
  return n.toFixed(n >= 100 ? 0 : n >= 10 ? 1 : 2);
};

const clock = (sec) => {
  const s = Math.max(0, Math.floor(sec || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}`;
  return `${m}:${String(r).padStart(2, '0')}`;
};

const haptic = (kind) => {
  try {
    const hf = window.Telegram?.WebApp?.HapticFeedback;
    if (kind === 'select') hf?.selectionChanged();
    else hf?.notificationOccurred(kind);
  } catch (_) {
    /* вне Telegram просто нет вибрации */
  }
};

/* ------------------------------------------------------------------ атомы */

const TierBadge = memo(function TierBadge({ code, label, color, size = 'sm' }) {
  if (!label) return null;
  return (
    <span className={`duel-tier duel-tier-${size}`} style={{ '--tier': color || '#c4b5a0' }}>
      <i className="duel-tier-shield" aria-hidden />
      {label}
    </span>
  );
});

/** Форма соперника: пять точек, свежая слева. Читается быстрее любых цифр. */
const FormDots = memo(function FormDots({ form }) {
  if (!form?.length) return null;
  return (
    <span className="duel-form" aria-hidden>
      {form.slice(0, 5).map((r, i) => (
        <i key={i} className={`duel-form-dot is-${r}`} />
      ))}
    </span>
  );
});

/** Число, которое «докручивается» до нового значения и вспыхивает при росте. */
const RollingScore = memo(function RollingScore({ value, className = '' }) {
  const target = num(value);
  const [shown, setShown] = useState(target);
  const [bump, setBump] = useState(false);
  const fromRef = useRef(target);
  const rafRef = useRef(0);

  useEffect(() => {
    const from = fromRef.current;
    if (Math.abs(target - from) < 0.005) return undefined;
    if (target > from) {
      setBump(true);
      setTimeout(() => setBump(false), 420);
    }
    const start = performance.now();
    const dur = 520;
    const step = (t) => {
      const k = Math.min(1, (t - start) / dur);
      // ease-out: быстро стартует, мягко приходит — так глаз успевает
      // заметить сам факт прироста, а не только новое число
      const e = 1 - (1 - k) ** 3;
      setShown(from + (target - from) * e);
      if (k < 1) rafRef.current = requestAnimationFrame(step);
      else fromRef.current = target;
    };
    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
  }, [target]);

  return <span className={`duel-roll ${bump ? 'is-bump' : ''} ${className}`}>{fmtNum(shown)}</span>;
});

const Countdown = memo(function Countdown({ endsAt, secondsLeft, total, onDone }) {
  const [left, setLeft] = useState(secondsLeft || 0);
  const doneRef = useRef(false);

  const anchorRef = useRef({ at: 0, base: 0 });

  useEffect(() => {
    doneRef.current = false;
    const at = Date.now();
    const base = endsAt
      ? Math.max(0, (new Date(endsAt).getTime() - at) / 1000)
      : secondsLeft || 0;
    anchorRef.current = { at, base };
    setLeft(base);
  }, [endsAt, secondsLeft]);

  const tick = useCallback(() => {
    const { at, base } = anchorRef.current;
    const next = endsAt
      ? Math.max(0, (new Date(endsAt).getTime() - Date.now()) / 1000)
      : Math.max(0, base - (Date.now() - at) / 1000);
    setLeft(next);
    if (next <= 0 && !doneRef.current) {
      doneRef.current = true;
      onDone?.();
    }
  }, [endsAt, onDone]);

  // Полоска считает оставшееся время от абсолютной отметки, поэтому пауза в
  // фоне не сбивает счёт — на возврате хук сразу дёргает tick.
  useVisibleInterval(tick, 250);

  const urgent = left <= 30;
  const pct = total ? Math.max(0, Math.min(100, (left / total) * 100)) : 0;
  return (
    <div className={`duel-timer ${urgent ? 'is-urgent' : ''}`}>
      <span className="duel-clock">{clock(left)}</span>
      <span className="duel-timer-track" aria-hidden>
        <i style={{ width: `${pct}%` }} />
      </span>
    </div>
  );
});

/* ------------------------------------------------------------------ арена */

const Fighter = memo(function Fighter({ player, side, score, taps, leading, momentum, t }) {
  if (!player) return null;
  return (
    <div className={`duel-fighter duel-fighter-${side} ${leading ? 'is-leading' : ''}`}>
      <div className="duel-fighter-ava">
        <span className="duel-ava-halo" aria-hidden />
        <Avatar
          userData={{ photo_url: player.photo_url, first_name: player.name }}
          size={64}
          className="duel-avatar"
        />
        {leading ? <span className="duel-crown" aria-hidden /> : null}
        {momentum > 0 ? <span className="duel-momentum" aria-hidden>▲</span> : null}
      </div>
      <div className="duel-fighter-name">{player.name}</div>
      <TierBadge code={player.tier} label={player.tier_label} color={player.tier_color} />
      <FormDots form={player.form} />
      <RollingScore value={score} className={`duel-fighter-score side-${side}`} />
      <div className="duel-fighter-taps">
        {taps} {t('duelTaps')}
      </div>
    </div>
  );
});

/** Перетягивание каната: середина уходит к тому, кто вкладывает больше. */
const TugBar = memo(function TugBar({ mine, theirs }) {
  const a = Math.max(0, num(mine));
  const b = Math.max(0, num(theirs));
  const total = a + b;
  const share = total > 0 ? (a / total) * 100 : 50;
  return (
    <div className="duel-tug" role="img" aria-label={`${fmtNum(mine)} : ${fmtNum(theirs)}`}>
      <i className="duel-tug-mine" style={{ width: `${share}%` }} />
      <i className="duel-tug-theirs" style={{ width: `${100 - share}%` }} />
      <span className="duel-tug-knob" style={{ left: `${share}%` }} aria-hidden>
        <b />
      </span>
      <span className="duel-tug-center" aria-hidden />
    </div>
  );
});

const Arena = ({ duel, onGoTap, onRefresh, t }) => {
  const mine = num(duel.me.score);
  const theirs = num(duel.foe.score);
  const lead = mine - theirs;
  const total = duel.duration_seconds || 0;
  const left = duel.seconds_left || 0;
  const endgame = total ? 1 - left / total : 0;
  const suddenDeath = left <= 30 && left > 0;

  // Импульс: кто прибавляет прямо сейчас. Считаем на клиенте по разнице между
  // опросами — на сервере для этого пришлось бы хранить историю счёта.
  const prev = useRef({ mine, theirs, at: Date.now() });
  const [momentum, setMomentum] = useState({ mine: 0, theirs: 0 });
  const [flash, setFlash] = useState('');
  const leadSign = useRef(Math.sign(lead));

  useEffect(() => {
    const dt = (Date.now() - prev.current.at) / 1000;
    if (dt > 0.5) {
      setMomentum({
        mine: (mine - prev.current.mine) / dt,
        theirs: (theirs - prev.current.theirs) / dt,
      });
      prev.current = { mine, theirs, at: Date.now() };
    }
    const sign = Math.sign(mine - theirs);
    if (sign !== leadSign.current && (sign !== 0 || leadSign.current !== 0)) {
      // Смена лидера — единственное событие в дуэли, которое стоит того,
      // чтобы дёрнуть игрока вибрацией и вспышкой.
      if (sign > 0) {
        setFlash('ahead');
        haptic('success');
      } else if (sign < 0) {
        setFlash('behind');
        haptic('warning');
      }
      leadSign.current = sign;
      const id = setTimeout(() => setFlash(''), 1400);
      return () => clearTimeout(id);
    }
    return undefined;
  }, [mine, theirs]);

  const status = lead > 0 ? t('duelAhead') : lead < 0 ? t('duelBehind') : t('duelEven');

  // Прогноз: экстраполируем текущий темп обеих сторон на остаток времени.
  // Показываем только когда темп уже измерен и время ещё есть — иначе это
  // будет уверенное предсказание из воздуха.
  const projection = useMemo(() => {
    if (left < 10 || (momentum.mine <= 0 && momentum.theirs <= 0)) return null;
    const pm = mine + Math.max(0, momentum.mine) * left;
    const pt = theirs + Math.max(0, momentum.theirs) * left;
    const gap = pm - pt;
    const scale = Math.max(1, pm + pt);
    return {
      mine: pm,
      theirs: pt,
      verdict: Math.abs(gap) / scale < 0.05 ? 'close' : gap > 0 ? 'win' : 'lose',
    };
  }, [mine, theirs, momentum, left]);

  return (
    <section
      className={`duel-arena ${lead > 0 ? 'is-ahead' : lead < 0 ? 'is-behind' : ''} ${
        suddenDeath ? 'is-sudden' : ''
      } ${flash ? `flash-${flash}` : ''}`}
      style={{ '--endgame': endgame.toFixed(3) }}
    >
      <span className="duel-arena-bg" aria-hidden>
        <i className="duel-arena-grid" />
        <i className="duel-arena-glow" />
        <i className="duel-arena-embers" />
      </span>

      <div className="duel-arena-top">
        <span className="duel-format-chip">
          {duel.format_label}
          {duel.is_ranked ? null : <b className="duel-unranked">{t('duelCasual')}</b>}
        </span>
        <span className="duel-stake-chip">
          <CoinIcon size={14} />
          {fmtNum(num(duel.stake_amount) * 2)}
          <i className="duel-stake-note">{t('duelPot')}</i>
        </span>
      </div>

      <Countdown
        endsAt={duel.ends_at}
        secondsLeft={duel.seconds_left}
        total={total}
        onDone={onRefresh}
      />

      <div className="duel-fighters">
        <Fighter
          player={duel.me}
          side="me"
          score={duel.me.score}
          taps={duel.me.taps}
          leading={lead > 0}
          momentum={momentum.mine > momentum.theirs ? 1 : 0}
          t={t}
        />
        <div className="duel-vs">
          <span className="duel-vs-mark" aria-hidden>
            VS
          </span>
          <span className={`duel-lead ${lead > 0 ? 'up' : lead < 0 ? 'down' : ''}`}>
            {lead === 0 ? '—' : `${lead > 0 ? '+' : '−'}${fmtNum(Math.abs(lead))}`}
          </span>
        </div>
        <Fighter
          player={duel.foe}
          side="foe"
          score={duel.foe.score}
          taps={duel.foe.taps}
          leading={lead < 0}
          momentum={momentum.theirs > momentum.mine ? 1 : 0}
          t={t}
        />
      </div>

      <TugBar mine={duel.me.score} theirs={duel.foe.score} />

      <div className="duel-status-line">
        {suddenDeath ? <b className="duel-sudden-label">{t('duelFinalPush')}</b> : status}
      </div>

      {/* Прогноз по текущему темпу. Считается на клиенте из той же разницы
          между опросами, что и стрелки импульса: игрок должен понимать, хватит
          ли ему нынешнего темпа, пока ещё есть время что-то изменить. */}
      {projection ? (
        <div className={`duel-projection is-${projection.verdict}`}>
          <span className="duel-projection-label">{t('duelProjection')}</span>
          <b>
            {fmtNum(projection.mine)} : {fmtNum(projection.theirs)}
          </b>
          <i>
            {projection.verdict === 'win'
              ? t('duelProjectionWin')
              : projection.verdict === 'lose'
                ? t('duelProjectionLose')
                : t('duelProjectionClose')}
          </i>
        </div>
      ) : null}

      {duel.rating_stake?.ranked ? (
        <div className="duel-stakes-line">
          <span>
            {t('duelAtStake')}
            <b className="pot">
              <CoinIcon size={13} />
              {fmtNum(num(duel.stake_amount) * 2)}
            </b>
          </span>
          <span>
            {t('duelRating')}
            <b className="up">+{duel.rating_stake.win}</b>
            <i>/</i>
            <b className="down">{duel.rating_stake.lose}</b>
          </span>
        </div>
      ) : null}

      <button type="button" className="action-btn duel-cta" onClick={onGoTap}>
        <span className="duel-cta-spark" aria-hidden />
        {t('duelGoTap')}
      </button>
      <p className="duel-hint">{t('duelArenaHint')}</p>
    </section>
  );
};

/* ------------------------------------------------------------------ лобби */

const RatingRing = memo(function RatingRing({ rating, progress, color }) {
  const R = 34;
  const C = 2 * Math.PI * R;
  const dash = (Math.max(0, Math.min(100, progress)) / 100) * C;
  return (
    <div className="duel-ring" style={{ '--tier': color }}>
      <svg viewBox="0 0 80 80" aria-hidden>
        <circle className="duel-ring-track" cx="40" cy="40" r={R} />
        <circle
          className="duel-ring-fill"
          cx="40"
          cy="40"
          r={R}
          strokeDasharray={`${dash} ${C - dash}`}
        />
      </svg>
      <span className="duel-ring-value">{rating}</span>
    </div>
  );
});

const RatingPanel = memo(function RatingPanel({ rating, t }) {
  const placement = rating.placement_left > 0;
  return (
    <section className="duel-rating soft-card glass-panel">
      <RatingRing
        rating={rating.rating}
        progress={rating.tier_progress}
        color={rating.tier_color}
      />
      <div className="duel-rating-meta">
        <TierBadge
          code={rating.tier}
          label={rating.tier_label}
          color={rating.tier_color}
          size="lg"
        />
        <div className="duel-rating-record">
          <span className="rec-w">{rating.wins}</span>
          <i>·</i>
          <span className="rec-l">{rating.losses}</span>
          {rating.draws ? (
            <>
              <i>·</i>
              <span className="rec-d">{rating.draws}</span>
            </>
          ) : null}
          {rating.win_rate ? <span className="duel-winrate">{rating.win_rate}%</span> : null}
          {rating.streak > 1 ? (
            <span className="duel-streak">
              <FloatIcon src={iconUrl('fire.png')} size={13} native eager />
              {rating.streak}
            </span>
          ) : null}
        </div>
        <div className="duel-rating-sub">
          {placement
            ? t('duelPlacement').replace('{n}', rating.placement_left)
            : rating.next_tier
              ? t('duelToNextTier')
                  .replace('{tier}', rating.next_tier_label)
                  .replace('{n}', Math.max(0, rating.next_tier_at - rating.rating))
              : t('duelTopTier')}
        </div>
      </div>
    </section>
  );
});

const FormatPicker = memo(function FormatPicker({ formats, value, onChange }) {
  return (
    <div className="duel-formats" role="tablist">
      {(formats || []).map((f) => (
        <button
          key={f.code}
          type="button"
          role="tab"
          aria-selected={value === f.code}
          className={`duel-format fmt-${f.code} ${value === f.code ? 'is-active' : ''}`}
          onClick={() => {
            haptic('select');
            onChange(f.code);
          }}
        >
          <span className="duel-format-name">{f.label}</span>
          <span className="duel-format-time">{clock(f.seconds)}</span>
          <span className="duel-format-glow" aria-hidden />
        </button>
      ))}
    </div>
  );
});

const StakePicker = memo(function StakePicker({ presets, value, min, max, balance, onChange, t }) {
  const pct = max > min ? ((value - min) / (max - min)) * 100 : 0;
  return (
    <div className="duel-stakes">
      <div className="duel-stakes-head">
        <span>{t('duelStake')}</span>
        <b className={balance < value ? 'is-short' : ''}>
          <CoinIcon size={14} />
          {fmtNum(value)}
        </b>
      </div>
      <input
        type="range"
        className="duel-slider"
        min={min}
        max={max}
        step={Math.max(1, Math.round((max - min) / 100))}
        value={Math.min(max, Math.max(min, value))}
        style={{ '--pct': `${pct}%` }}
        onChange={(e) => onChange(Number(e.target.value))}
        aria-label={t('duelStake')}
      />
      <div className="duel-stake-row">
        {presets.map((p) => (
          <button
            key={p}
            type="button"
            className={`duel-stake ${Math.abs(value - p) < 0.001 ? 'is-active' : ''}`}
            onClick={() => {
              haptic('select');
              onChange(p);
            }}
          >
            {fmtNum(p)}
          </button>
        ))}
      </div>
    </div>
  );
});

const DuelRow = ({ row, t, actions }) => (
  <div className={`duel-row soft-card outcome-${row.outcome || 'none'}`}>
    <Avatar
      userData={{ photo_url: row.foe?.photo_url, first_name: row.foe?.name }}
      size={38}
      className="duel-row-ava"
    />
    <div className="duel-row-text">
      <span className="duel-row-name">
        {row.foe?.name || t('duelSearchingOpponent')}
        <TierBadge code={row.foe?.tier} label={row.foe?.tier_label} color={row.foe?.tier_color} />
      </span>
      <span className="duel-row-meta">
        <span className="duel-row-format">{row.format_label}</span>
        <span className="duel-row-stake">
          <CoinIcon size={12} />
          {fmtNum(row.stake_amount)}
        </span>
        {row.outcome ? (
          <span className={`duel-row-score outcome-${row.outcome}`}>
            {fmtNum(row.me.score)} : {fmtNum(row.foe.score)}
          </span>
        ) : null}
        <FormDots form={row.foe?.form} />
      </span>
    </div>
    {row.outcome && row.is_ranked ? (
      <span className={`duel-row-delta outcome-${row.outcome}`}>
        {row.rating_delta > 0 ? `+${row.rating_delta}` : row.rating_delta}
      </span>
    ) : null}
    {actions}
  </div>
);

const LadderRow = memo(function LadderRow({ row, isMe }) {
  return (
    <div className={`duel-ladder-row soft-card ${isMe ? 'is-me' : ''} ${row.rank <= 3 ? `top-${row.rank}` : ''}`}>
      <span className="duel-ladder-rank">{row.rank}</span>
      <Avatar userData={{ photo_url: row.photo_url, first_name: row.name }} size={34} />
      <div className="duel-ladder-text">
        <span className="duel-ladder-name">{row.name}</span>
        <TierBadge code={row.tier} label={row.tier_label} color={row.tier_color} />
      </div>
      <div className="duel-ladder-num">
        <b style={{ color: row.tier_color }}>{row.rating}</b>
        <i>
          {row.wins}/{row.losses}
        </i>
      </div>
    </div>
  );
});

/* ------------------------------------------------------------------ итог */

/* ------------------------------------------------------------------ экран */

const Duels = ({ clickerData, refreshClicker }) => {
  const { t } = useLocale();
  const navigate = useNavigate();
  const location = useLocation();
  const { alert, closeAlert, showError } = useGameAlert();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('play');
  const [ladder, setLadder] = useState(null);
  const [format, setFormat] = useState('standard');
  const [stake, setStake] = useState(50);
  const [busy, setBusy] = useState('');
  const [target, setTarget] = useState(location.state?.opponent || null);

  useEffect(() => subscribeDuel((next) => setData(next)), []);

  useEffect(() => {
    if (tab !== 'ladder' || ladder) return;
    api
      .duelTop()
      .then(setLadder)
      .catch(() => setLadder({ items: [] }));
  }, [tab, ladder]);

  const balance = num(clickerData?.balance);
  const spec = useMemo(
    () => (data?.formats || []).find((f) => f.code === format) || null,
    [data, format]
  );

  // Границы ползунка задаёт формат, а не текущий баланс. Раньше верхом был
  // баланс — и поскольку он тикает каждую секунду (пассивный доход, тапы),
  // выбранная ставка молча уползала следом. Игрок ставил 50, а отправлялось
  // другое число. Нехватку монет показываем отдельно: цифра краснеет, кнопка
  // гаснет, но выставленное значение остаётся ровно тем, что выставили.
  const bounds = useMemo(() => {
    const min = spec ? num(spec.min_stake) : 1;
    const max = spec ? num(spec.max_stake) : 1000;
    return { min, max: Math.max(min, max) };
  }, [spec]);

  // Быстрые кнопки, наоборот, считаются от кошелька: это подсказки «поставить
  // столько-то от того, что есть». Балансом их пересчитывать безопасно —
  // выбранное значение они не трогают, пока по ним не нажали.
  const presets = useMemo(() => {
    const { min, max } = bounds;
    const raw = [min, balance * 0.05, balance * 0.15, balance * 0.35];
    return raw
      .map((v) => Math.max(min, Math.min(max, Math.round(v))))
      .filter((v, i, arr) => v > 0 && arr.indexOf(v) === i)
      .sort((a, b) => a - b);
  }, [bounds, balance]);

  // Подтягиваем ставку в границы только при смене формата — и только если она
  // из них вышла. Любой пересчёт «на всякий случай» здесь означал бы, что
  // число под пальцем меняется само.
  useEffect(() => {
    setStake((s) => {
      if (s >= bounds.min && s <= bounds.max) return s;
      return Math.max(bounds.min, Math.min(bounds.max, s));
    });
  }, [bounds.min, bounds.max]);


  const act = useCallback(
    async (key, fn) => {
      if (busy) return null;
      setBusy(key);
      try {
        const res = await fn();
        await refreshDuel({ silent: false });
        refreshClicker?.({ forceSync: true, reason: 'duel' }).catch(() => {});
        return res;
      } catch (err) {
        showError(err, t('duelFailed'));
        return null;
      } finally {
        setBusy('');
      }
    },
    [busy, refreshClicker, showError, t]
  );

  const onSearch = () =>
    act('search', async () => {
      const res = await api.duelMatchmake({ format, stake_type: 'coins', stake_amount: stake });
      if (res?.state === 'matched') {
        celebrate('level');
        haptic('success');
      }
      return res;
    });

  const onChallengeTarget = () =>
    act('challenge', async () => {
      const res = await api.pvpCreate({
        opponent_id: target.id,
        format,
        stake_type: 'coins',
        stake_amount: stake,
      });
      setTarget(null);
      return res;
    });



  const rating = data?.rating;
  const active = data?.active;
  const searching = data?.searching;
  const locked = Boolean(active || searching);
  const canPay = balance >= stake;

  return (
    <main className="tasks-screen duel-screen screen-enter">
      <ScreenTitle
        icon={iconUrl('muscle.png')}
        title={t('duelsTitle')}
        subtitle={active ? t('duelsSubtitleActive') : t('duelsSubtitle')}
      />

      {!data ? <div className="duel-skeleton" aria-hidden /> : null}

      {active ? (
        <Arena
          duel={active}
          t={t}
          onGoTap={() => navigate('/clicker')}
          onRefresh={() => refreshDuel()}
        />
      ) : null}

      {rating ? <RatingPanel rating={rating} t={t} /> : null}

      {data && !active ? (
        <div className="duel-tabs" role="tablist">
          {[
            ['play', t('duelTabPlay')],
            ['history', t('duelTabHistory')],
            ['ladder', t('duelTabLadder')],
          ].map(([id, label]) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={tab === id}
              className={`duel-tab ${tab === id ? 'is-active' : ''}`}
              onClick={() => {
                haptic('select');
                setTab(id);
              }}
            >
              {label}
            </button>
          ))}
        </div>
      ) : null}

      {!active && tab === 'play' ? (
        <section className="duel-setup soft-card glass-panel">
          <FormatPicker formats={data?.formats} value={format} onChange={setFormat} />
          {spec ? <p className="duel-format-hint">{spec.hint}</p> : null}
          <StakePicker
            presets={presets}
            value={stake}
            min={bounds.min}
            max={bounds.max}
            balance={balance}
            onChange={setStake}
            t={t}
          />

          {target ? (
            <div className="duel-target">
              <Avatar
                userData={{ photo_url: target.photo_url, first_name: target.name }}
                size={32}
              />
              <span>{t('duelChallengeTo').replace('{name}', target.name)}</span>
              <button type="button" className="duel-target-clear" onClick={() => setTarget(null)}>
                ✕
              </button>
            </div>
          ) : null}

          {searching ? (
            <div className="duel-searching">
              <span className="duel-radar" aria-hidden>
                <i />
                <i />
                <i />
                <b />
              </span>
              <div className="duel-searching-text">
                <b>{t('duelSearching')}</b>
                <span>{t('duelSearchingHint')}</span>
              </div>
              <button
                type="button"
                className="duel-mini-btn"
                disabled={busy === 'cancel'}
                onClick={() => act('cancel', () => api.pvpCancel(searching.id))}
              >
                {t('duelCancel')}
              </button>
            </div>
          ) : (
            <button
              type="button"
              className="action-btn duel-search-btn"
              disabled={Boolean(busy) || !canPay}
              onClick={target ? onChallengeTarget : onSearch}
            >
              <span className="duel-cta-spark" aria-hidden />
              {!canPay ? t('notEnoughCoins') : target ? t('duelSendChallenge') : t('duelFind')}
            </button>
          )}
        </section>
      ) : null}

      {data?.incoming?.length ? (
        <section className="duel-block">
          <h2 className="duel-block-title is-hot">{t('duelIncoming')}</h2>
          {data.incoming.map((row) => (
            <DuelRow
              key={row.id}
              row={row}
              t={t}
              actions={
                <span className="duel-row-actions">
                  <button
                    type="button"
                    className="duel-mini-btn is-accept"
                    disabled={Boolean(busy) || locked}
                    onClick={() => act(`acc${row.id}`, () => api.pvpAccept(row.id))}
                  >
                    {t('duelAccept')}
                  </button>
                  <button
                    type="button"
                    className="duel-mini-btn"
                    disabled={Boolean(busy)}
                    onClick={() => act(`dec${row.id}`, () => api.duelDecline(row.id))}
                  >
                    {t('duelDecline')}
                  </button>
                </span>
              }
            />
          ))}
        </section>
      ) : null}

      {!active && tab === 'play' && data?.open_queue?.length ? (
        <section className="duel-block">
          <h2 className="duel-block-title">{t('duelOpenQueue')}</h2>
          <p className="duel-block-hint">{t('duelOpenQueueHint')}</p>
          {data.open_queue.map((row) => (
            <DuelRow
              key={row.id}
              row={row}
              t={t}
              actions={
                <button
                  type="button"
                  className="duel-mini-btn is-accept"
                  disabled={Boolean(busy) || locked || balance < num(row.stake_amount)}
                  onClick={() => act(`take${row.id}`, () => api.duelTake(row.id))}
                >
                  {t('duelTake')}
                </button>
              }
            />
          ))}
        </section>
      ) : null}

      {!active && tab === 'play' && data?.outgoing?.length ? (
        <section className="duel-block">
          <h2 className="duel-block-title">{t('duelOutgoing')}</h2>
          {data.outgoing.map((row) => (
            <DuelRow
              key={row.id}
              row={row}
              t={t}
              actions={
                <button
                  type="button"
                  className="duel-mini-btn"
                  disabled={Boolean(busy)}
                  onClick={() => act(`can${row.id}`, () => api.pvpCancel(row.id))}
                >
                  {t('duelCancel')}
                </button>
              }
            />
          ))}
        </section>
      ) : null}

      {!active && tab === 'history' ? (
        <section className="duel-block">
          {data?.history?.length ? (
            data.history.map((row) => (
              <DuelRow
                key={row.id}
                row={row}
                t={t}
                actions={
                  row.foe?.id ? (
                    <button
                      type="button"
                      className="duel-mini-btn"
                      disabled={Boolean(busy) || locked}
                      onClick={() => act(`rm${row.id}`, () => api.duelRematch(row.id))}
                    >
                      {t('duelRematch')}
                    </button>
                  ) : null
                }
              />
            ))
          ) : (
            <p className="duel-empty">{t('duelEmpty')}</p>
          )}
        </section>
      ) : null}

      {!active && tab === 'ladder' ? (
        <section className="duel-block">
          {!ladder ? (
            <div className="duel-skeleton" aria-hidden />
          ) : ladder.items?.length ? (
            ladder.items.map((row) => (
              <LadderRow key={row.id} row={row} isMe={row.rank === ladder.me?.rank} />
            ))
          ) : (
            <p className="duel-empty">{t('duelLadderEmpty')}</p>
          )}
        </section>
      ) : null}

      <AlertModal
        open={Boolean(alert)}
        icon={alert?.icon}
        title={alert?.title}
        body={alert?.body}
        have={alert?.have}
        need={alert?.need}
        currency={alert?.currency}
        onClose={closeAlert}
        confirmLabel={t('gotIt')}
      />
    </main>
  );
};

export default Duels;
